import { Wand2, Shirt, Image as ImageIcon, ArrowRight } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/flowly/infra/Button/Button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { PROFILE_PHOTO_CUSTOM_FIELD_MAX } from "@/config/profilePhotoPresets";

interface ProfilePhotoCustomStyleModalProps {
  isOpen: boolean;
  background: string;
  clothing: string;
  onBackgroundChange: (value: string) => void;
  onClothingChange: (value: string) => void;
  onContinue: () => void;
  onClose: () => void;
}

/**
 * "Design your own" modal for the Profile Photo custom preset.
 *
 * Two required fields — background and clothing — each with example placeholder
 * text and a row of quick-pick suggestion chips. The orange Continue button
 * advances to the next step and is disabled until both fields are filled.
 */
export function ProfilePhotoCustomStyleModal({
  isOpen,
  background,
  clothing,
  onBackgroundChange,
  onClothingChange,
  onContinue,
  onClose,
}: ProfilePhotoCustomStyleModalProps) {
  const { t } = useLanguage();
  const c = t.profilePhoto;

  const backgroundChips = c.customBackgroundChips as readonly string[];
  const clothingChips = c.customClothingChips as readonly string[];

  const canContinue =
    background.trim().length > 0 && clothing.trim().length > 0;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-xl bg-background/95 backdrop-blur-xl border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-start flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-brand text-brand-foreground flex items-center justify-center shadow-sm">
              <Wand2 className="w-5 h-5" />
            </span>
            {c.customTitle}
          </DialogTitle>
          <DialogDescription className="text-start">
            {c.customExplanation}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <CustomField
            icon={ImageIcon}
            label={c.customBackgroundLabel}
            placeholder={c.customBackgroundPlaceholder}
            value={background}
            onChange={onBackgroundChange}
            chips={backgroundChips}
            chipsLabel={c.customChipsHint}
          />

          <CustomField
            icon={Shirt}
            label={c.customClothingLabel}
            placeholder={c.customClothingPlaceholder}
            value={clothing}
            onChange={onClothingChange}
            chips={clothingChips}
            chipsLabel={c.customChipsHint}
          />

          <Button
            variant="primary"
            size="lg"
            className="w-full bg-brand hover:bg-brand/90 text-brand-foreground glow-primary disabled:opacity-50"
            onClick={onContinue}
            disabled={!canContinue}
          >
            {c.customContinue}
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CustomField({
  icon: Icon,
  label,
  placeholder,
  value,
  onChange,
  chips,
  chipsLabel,
}: {
  icon: typeof Wand2;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  chips: readonly string[];
  chipsLabel: string;
}) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-start text-sm font-semibold text-foreground">
          <Icon className="w-4 h-4 text-brand" />
          {label}
        </Label>
        <span className="text-[11px] tabular-nums text-muted-foreground/80">
          {value.length} / {PROFILE_PHOTO_CUSTOM_FIELD_MAX}
        </span>
      </div>

      <Input
        type="text"
        maxLength={PROFILE_PHOTO_CUSTOM_FIELD_MAX}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />

      <div>
        <p className="mb-1.5 text-[11px] text-muted-foreground">{chipsLabel}</p>
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => {
            const isActive = value.trim() === chip;
            return (
              <button
                key={chip}
                type="button"
                onClick={() => onChange(chip)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                  isActive
                    ? "bg-brand text-brand-foreground border-brand"
                    : "bg-card text-foreground border-border hover:border-brand/50 hover:bg-brand/5",
                )}
              >
                {chip}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
